/* Строго одна строка: полоса не переносится ни на какой ширине. Всё, что
   не влезает, ужимается за счёт подписей, а не за счёт переноса. */
.toolbar {
  /* Полоса живёт на 20% крупнее остального интерфейса: это шапка, а не рабочая
     плотная область. Шкала переопределена целыми пикселями (12→14, 13→16,
     15→18) и по-прежнему множится на --fs-scale, поэтому размер шрифта из
     персонализации продолжает работать. Модалы уезжают порталом в body и
     этих значений не наследуют. */
  --fs-sm: calc(14px * var(--fs-scale));
  --fs-md: calc(16px * var(--fs-scale));
  --fs-lg: calc(18px * var(--fs-scale));

  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--sp-5);
  /* Верхний отступ с запасом под чёлку: на планшете с домашнего экрана полоса
     стоит у самой кромки экрана. В браузере env(...) равен нулю, и остаётся
     ровно прежний --sp-3 (см. index.html про viewport-fit). */
  padding: calc(var(--sp-3) + env(safe-area-inset-top, 0px)) var(--sp-5) var(--sp-3);
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  /* Прокрутка живёт на .settings, а не здесь. overflow-x: auto на самой полосе
     по спецификации делает `auto` и вторую ось, то есть полоса начинает резать
     всё, что свисает вниз, — а вниз свисают меню снимков и всплывающее
     сообщение о сохранении. Видно от них оставалось три пикселя. */
}

/* Название слева, действия справа делят остаток поровну — так группа контролов
   стоит ровно по центру полосы, а не сразу за названием. */
.brand {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  flex: 1 1 0;
  /* Ужиматься можно только до собственного минимума: за ним название уже
     сокращено до 3ch, и дальше флаг языка наезжал бы на соседние контролы.
     Что не влезло — уезжает в горизонтальную прокрутку полосы. */
  min-width: min-content;
}

/* Логотип сжимается первым, но не до нуля: минимум оставляем читаемым.
   Размеры и цвет знака живут в Logo.module.css. */
.brandLogo {
  flex: 0 1 auto;
  min-width: 6ch;
}

/* Шаг между группами тот же, что внутри них: иначе стык «+» и обмена валют
   выпадал вдвое шире остальных и читался как дырка. Группы всё равно
   различимы — каждая начинается со своей иконки. */
.settings {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--sp-2);
  /* Когда контролы совсем не влезают, вбок уезжает именно эта группа: перенос
     запрещён, а сжимать кнопки до наложения друг на друга нельзя. Ничего
     свисающего вниз здесь нет, поэтому клип по вертикали безвреден. */
  flex: 0 1 auto;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.settings::-webkit-scrollbar {
  display: none;
}

/* Подпись поля ушла в подсказку: в одну строку помещается только сам контрол. */
.field {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex: none;
  white-space: nowrap;
}

/* Единственная иконка полосы без кнопки под собой: приглушённым цветом она
   читалась как подпись, а не как обозначение того, чем управляет степпер
   рядом. Поэтому берёт полный контраст текста и более жирный штрих. */
.fieldIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Без флекса svg садился бы на базовую линию текста и вставал бы на пару
     пикселей выше центра — рядом с кнопками степпера это сразу заметно. */
  height: var(--ctl-h);
  flex: none;
  color: var(--text);
}

/* Все контролы полосы — одного роста: она читается как одна строка, а не как
   набор кнопок разной высоты. Селектор с именем элемента обязателен — иначе
   одноклассовый .ctl проигрывает глобальным .btn и .select с их отступами. */
button.ctl,
select.ctl {
  height: var(--ctl-h);
  padding-top: 0;
  padding-bottom: 0;
}

/* По содержимому, без пола по ширине: в поле стоит код из трёх букв и символ,
   а фиксированные 96px оставляли перед стрелкой полтора пустых знакоместа. */
select.ctl {
  width: auto;
  min-width: 0;
}

/* Кнопки без подписи — квадраты той же высоты, что и остальная полоса. */
button.ctlIcon {
  width: var(--ctl-h);
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}

.stepper {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}

.stepperValue {
  min-width: 1.5em;
  text-align: center;
  font-weight: 600;
}

/* Снимки и аккаунт стоят рядом, но это разные вещи: одно про поездку, другое
   про человека. Промежуток между ними равен стороне самой кнопки (--ctl-h),
   а не обычному шагу сетки: на общем шаге три иконки читались одним рядом, и
   кнопка аккаунта выглядела третьим снимком. */
.actions {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--ctl-h);
  flex: 1 1 0;
  min-width: 0;
}

.hiddenInput {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
}

.errorList {
  margin: var(--sp-4) 0 0;
  padding-left: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  font-size: var(--fs-sm);
}

/* Первым при нехватке места сдаётся логотип — контролы важнее.
   Уменьшение самого знака описано в Logo.module.css. */
@media (max-width: 720px) {
  .brandLogo {
    min-width: 4ch;
  }
}

/* ── Мобильный вид (порог продублирован в useIsMobile.ts) ─────────────────
   Саму мобильную полосу и сайдбар рисует MobileShellHeader — здесь остаётся
   только размер контролов раздела «Поездка» внутри его сайдбара: их
   разметку (AppMenuRow) держит ui/shell/AppMenu, а под палец, а не под
   курсор, их доразмеряет вот этот класс. */
.menuRow button.ctl,
.menuRow select.ctl {
  height: 48px;
}

.menuRow button.ctlIcon {
  width: 48px;
}

/* Кнопки снимков приходят сюда слотом и своих размеров под палец не знают:
   в полосе они меряются высотой строки тулбара, рассчитанной на курсор. */
.menuRow :global(.btn) {
  min-height: 48px;
}
